import { Resources } from 'constants/resources';

import { useEffect } from 'react';

import { useGatewaysCurrencyIds } from 'hooks/useGatewaysCurrencyIds';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required, useGetList, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { AutocompleteInput } from 'shared/react-admin/Inputs';
import { Currency } from 'types/embedded/currency';
import { MerchantAccount } from 'types/embedded/merchantAccount';

type Props = {
  disabled: boolean;
  gatewayIds: string[];
};

export const MerchantShowAccountsCurrencyInput: React.FC<Props> = ({ disabled, gatewayIds }) => {
  const translate = useTypedTranslate();

  const record = useRecordContext<MerchantAccount>();
  const { setValue, watch } = useFormContext();
  const currency = watch('currency');

  const currencyIds = useGatewaysCurrencyIds(gatewayIds);
  const { data: currencies = [] } = useGetList<Currency>(
    Resources.Currencies,
    {
      filter: { id: currencyIds, active: true },
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'id', order: 'ASC' },
    },
    { enabled: currencyIds.length > 0 },
  );

  const savedCurrency = record?.currency;
  const isSelectedInList = currencies.some((c) => c['@id'] === currency);
  const previousCurrency =
    !isSelectedInList && savedCurrency?.['@id'] === currency ? savedCurrency : undefined;

  const choices = previousCurrency ? [...currencies, previousCurrency] : currencies;

  const singleCurrencyId = currencies.length === 1 ? currencies[0]['@id'] : undefined;

  useEffect(() => {
    if (singleCurrencyId && !currency) {
      setValue('currency', singleCurrencyId);
    }
  }, [singleCurrencyId, currency, setValue]);

  return (
    <AutocompleteInput
      choices={choices}
      disabled={disabled}
      fullWidth
      isRequired
      label={translate('app.merchantAccounts.currency')}
      optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
      optionValue="@id"
      source="currency"
      validate={required()}
      variant="outlined"
    />
  );
};
